import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#approach", label: "Our Approach" },
  { href: "#services", label: "Services" },
  { href: "#expertise", label: "Expertise" },
  { href: "#partners", label: "Partners" },
  { href: "#team", label: "Join Us" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0A192F]/92 backdrop-blur-xl border-b border-white/10 shadow-[0_2px_24px_rgba(0,0,0,0.18)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("#hero")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/sunrise-logo.png"
            alt="Sunrise Wealth Management"
            className="h-14 w-auto object-contain transition-transform group-hover:-translate-y-0.5"
          />
          <span className="hidden sm:flex items-center">
            <span className="sr-only">Sunrise Wealth Management</span>
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.href}>
              <button
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => scrollTo(l.href)}
                className="text-sm text-white/75 hover:text-[#F5A623] transition-colors duration-300 tracking-wide"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-consultation"
            onClick={() => scrollTo("#contact")}
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F5A623] text-[#0A192F] text-sm font-medium tracking-wide hover:bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            Book Consultation
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#0A192F]/97 backdrop-blur-xl border-b border-white/10"
          >
            <ul className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <li key={l.href}>
                  <button
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => scrollTo(l.href)}
                    className="text-base text-white hover:text-[#F5A623] transition-colors w-full text-left"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <button
                  data-testid="mobile-book-consultation"
                  onClick={() => scrollTo("#contact")}
                  className="mt-2 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F5A623] text-[#0A192F] text-sm font-medium w-full justify-center"
                >
                  Book Free Consultation
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
